/**
 * IMPORTS
 */
import { type ReactNode, useEffect, useRef } from "react";

import { FileText, X } from "lucide-react";

export type CampoDetalheRemessa = {
  label: string;
  value: ReactNode;
};

export type SecaoDetalhesRemessa = {
  titulo: string;
  campos: CampoDetalheRemessa[];
};

type ModalDetalhesRemessaProps = {
  titulo: string;
  subtitulo?: string;
  secoes: SecaoDetalhesRemessa[];
  onFechar: () => void;
  /** Conteúdo opcional no rodapé (ex.: botões de ação da tela). */
  rodape?: ReactNode;
};

const VALOR_VAZIO = "N/A";

function exibirValorOuNA(valor: ReactNode) {
  if (valor === null || valor === undefined || valor === "") return VALOR_VAZIO;
  return valor;
}

/**
 * Modal somente leitura com os detalhes completos de uma remessa.
 * Cada tela monta suas próprias seções; o modal só cuida do layout.
 */
function ModalDetalhesRemessa({
  titulo,
  subtitulo,
  secoes,
  onFechar,
  rodape,
}: ModalDetalhesRemessaProps) {
  // fecha com ESC
  useEffect(() => {
    const fecharComEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") onFechar();
    };
    window.addEventListener("keydown", fecharComEsc);
    return () => window.removeEventListener("keydown", fecharComEsc);
  }, [onFechar]);

  // selecionar texto dentro do modal e soltar o mouse no fundo dispara click no fundo;
  // só fecha quando o clique começou no próprio fundo
  const cliqueComecouNoFundo = useRef(false);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(event) => {
        cliqueComecouNoFundo.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (cliqueComecouNoFundo.current && event.target === event.currentTarget) onFechar();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-detalhes-remessa-titulo"
        className="max-h-[90vh] w-full max-w-[840px] space-y-5 overflow-y-auto rounded-xl bg-neutral-50 px-6 py-5 shadow-shape"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2">
            <FileText className="mt-1 shrink-0 text-zinc-800" size={24} />
            <div>
              <h2
                id="modal-detalhes-remessa-titulo"
                className="text-xl font-semibold text-zinc-800"
              >
                {titulo}
              </h2>
              {subtitulo && <p className="text-sm text-zinc-500">{subtitulo}</p>}
            </div>
          </div>
          <button type="button" onClick={onFechar} aria-label="Fechar">
            <X className="size-5 text-zinc-400" />
          </button>
        </div>

        {secoes.map((secao) => (
          <section key={secao.titulo} className="space-y-3">
            <h3 className="border-b border-zinc-200 pb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">
              {secao.titulo}
            </h3>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              {secao.campos.map((campo) => (
                <div key={campo.label} className="min-w-0">
                  <dt className="text-xs text-zinc-500">{campo.label}</dt>
                  <dd className="whitespace-pre-wrap break-words text-sm font-medium text-zinc-800">
                    {exibirValorOuNA(campo.value)}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        {rodape && (
          <div className="flex justify-end gap-2 border-t border-zinc-200 pt-4">{rodape}</div>
        )}
      </div>
    </div>
  );
}

/**
 * EXPORTS
 */
export { ModalDetalhesRemessa };
